import { Workflow, Info } from 'lucide-react'
import { DocSection } from '@/components/docs/doc-section'
import { DocPageHeader } from '@/components/docs/doc-page-header'
import { DocNav } from '@/components/docs/doc-nav'
import { MermaidDiagram } from '@/components/docs/mermaid-diagram'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'

export function EvaluationPage() {
  return (
    <>
      <DocPageHeader icon={Workflow} title="Request Evaluation" />

      <div className="flex flex-1 flex-col gap-6 overflow-auto p-6 max-w-4xl">
        <DocSection title="How a Request is Evaluated">
          <p className="text-sm text-muted-foreground leading-relaxed">
            When a user attempts an action — such as running a SQL query against a database — the PDP
            follows a structured evaluation process to reach a decision. Understanding this process
            helps you design effective policies and diagnose unexpected access results.
          </p>
        </DocSection>

        <DocSection title="Evaluation Steps">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">Step 1</Badge>
                  <CardTitle className="text-sm">Target Matching</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  The PDP checks each policy's target against the incoming request. Only policies
                  whose targets match the request's attributes are considered. Policies that don't
                  match are skipped — their result is "Not Applicable". This keeps evaluation
                  efficient: if you have hundreds of policies, only the relevant ones are processed.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">Step 2</Badge>
                  <CardTitle className="text-sm">Rule Evaluation</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  For each applicable policy, the PDP evaluates every rule's condition. Each
                  condition is an expression over the request's attributes (e.g.,{' '}
                  <code className="text-xs bg-muted px-1 py-0.5 rounded">
                    subject.department == "finance"
                  </code>
                  ). Rules whose conditions are true fire with their declared effect — Permit or
                  Deny. Rules whose conditions are false are ignored.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">Step 3</Badge>
                  <CardTitle className="text-sm">Combining</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  The combining algorithm merges individual rule results into a single policy
                  decision. For example, with <strong className="text-foreground">deny-overrides</strong>:
                  if any rule returns Deny, the policy result is Deny — regardless of how many other
                  rules returned Permit. The algorithm you choose on each policy directly shapes its
                  security posture.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">Step 4</Badge>
                  <CardTitle className="text-sm">Decision + Obligations</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  The final decision (Permit or Deny) is returned along with any obligations.
                  Obligations are enforcement instructions that the PEP must carry out — for example,
                  "mask the SSN column" or "apply row filter:{' '}
                  <code className="text-xs bg-muted px-1 py-0.5 rounded">
                    department = 'finance'
                  </code>
                  ". This is how poli-x enforces fine-grained controls like column masking and
                  row-level security.
                </p>
              </CardContent>
            </Card>
          </div>
        </DocSection>

        <MermaidDiagram
          src="/docs/diagrams/evaluation-flowchart.svg"
          alt="Policy evaluation flowchart from request to final decision"
          caption="Policy evaluation flowchart"
        />

        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>Key Concept</AlertTitle>
          <AlertDescription className="leading-relaxed">
            If no policy targets match the request, the result is "Not Applicable". In poli-x, the
            PEP treats this as a Deny by default — this is called{' '}
            <strong className="text-foreground">fail-closed</strong> behavior, ensuring that
            unrecognized requests are never accidentally permitted. You don't need to write explicit
            deny-all policies; the system denies by default unless a policy explicitly permits
            access.
          </AlertDescription>
        </Alert>

        <DocNav
          prev={{ title: 'Policies', url: '/docs/policies' }}
          next={{ title: 'Architecture', url: '/docs/architecture' }}
        />
      </div>
    </>
  )
}
